import {
  ApolloCache,
  ApolloClient,
  OperationVariables,
  useApolloClient,
  useLazyQuery,
} from '@apollo/client'
import { Modifiers } from '@apollo/client/cache/core/types/common'
import { StoreValue } from '@apollo/client/utilities/graphql/storeUtils'
import { DocumentNode } from 'graphql/language/ast'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import isString from 'lodash/isString'
import keys from 'lodash/keys'
import uniqBy from 'lodash/uniqBy'
import { MutableRefObject, useEffect, useRef } from 'react'

import { parseVariables } from './'

type Field = {
  fieldName: string
  fragment: DocumentNode
  fragmentName?: string
  // path to get data of api response
  responsePath?: string
  // custom generate note, use for deep value of api response
  generateNode?: (cache: ApolloCache<unknown>, data: {}) => unknown
  // key to check uniq cacheID value
  uniqByKey?: string
}

/**
 * @description auto get new data if user go back the page
 * @param query
 * @param variables
 * @param fields
 * @param identify cacheID
 * @param onCompleted
 */
export const useGetNewestData = <TData, TVariables extends OperationVariables>({
  query,
  variables,
  fields,
  identify,
  onCompleted,
}: {
  query: DocumentNode
  variables: TVariables
  fields: Field[]
  identify?: {
    __typename?: string
    /**
     * @description if you want to get id from api response, then pass function to id
     * @example
     * identify: identify: {__typename: 'xxx', id: (apiResponse) => apiResponse.otherField.id}
     */
    id: string | ((data: TData | undefined) => StoreValue)
  }
  onCompleted?: (data: TData | undefined) => void
}): { isFirstLoadRef: MutableRefObject<boolean> } => {
  const isFirstLoadRef = useRef<boolean>(true)
  const client = useApolloClient()

  const [getNewestData] = useLazyQuery<TData, TVariables>(query, {
    variables: variables,
    // Always call api and not save data into cache
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
    // handle and save data to cache
    onCompleted: storeNewestDataToCache(fields, identify, {
      variables,
      client,
      callback: onCompleted,
    }),
  })

  useEffect(() => {
    /**
     * @hack
     * When upgrading @apollo/client to a higher version, the hook execution flow changed.
     * I added a delay to ensure useQuery completes first
     */
    setTimeout(() => {
      if (!isFirstLoadRef.current) {
        getNewestData({})
      }
    }, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { isFirstLoadRef }
}

/**
 * @description
 * @param identify
 * @param {Field[]} fields
 * @param variables
 * @param client
 * @param callback
 * @example:
 * const [] = useLazyQuery({
 *   fetchPolicy: 'network-only',
 *   onCompleted: storeNewestDataToCache(fields, identify),
 * })
 */
const storeNewestDataToCache = <TData>(
  fields: Field[],
  identify:
    | {
        __typename?: string
        id: string | ((data: TData | undefined) => StoreValue)
      }
    | undefined,
  {
    variables,
    client,
    callback,
  }: {
    variables: OperationVariables
    client: ApolloClient<object>
    callback?: (data: TData | undefined) => void
  }
) => {
  return function onCompleted(data: TData | undefined) {
    const cache = client?.cache

    const cacheFields: Modifiers<Record<string, Record<string, unknown>>> = {}

    fields.forEach(
      ({
        fieldName,
        uniqByKey = 'node.__ref',
        fragment,
        fragmentName,
        generateNode,
        responsePath,
      }) => {
        cacheFields[fieldName] = (
          existingData = { edges: [] },
          { storeFieldName }
        ) => {
          // convert variable string to JSON object
          const inputs = parseVariables(storeFieldName)
          // check variables with storeFieldName have equal value
          if (inputs && !compareTwoObject(inputs, variables)) {
            return existingData
          }
          return {
            ...existingData,
            edges: uniqBy(
              [
                ...get(data, responsePath ?? fieldName, {
                  edges: [],
                }).edges.map(({ node: data }: { node: {} }) => ({
                  node: generateNode
                    ? generateNode(cache, data)
                    : cache.writeFragment({ fragment, data, fragmentName }),
                  __typename: 'Edge',
                })),
                ...get(existingData, 'edges', {}),
              ],
              uniqByKey
            ),
          }
        }
      }
    )
    cache?.modify({
      id: identify
        ? cache?.identify({
            __typename: identify.__typename,
            // return id from response data if id is function
            id: isString(identify.id) ? identify.id : identify.id(data),
          })
        : 'ROOT_QUERY',
      fields: cacheFields,
    })
    callback?.(data)
  }
}

function compareTwoObject<
  T extends Record<string, unknown>,
  F extends Record<string, unknown>
>(objectOne?: T, objectTwo?: F): boolean {
  return keys(objectOne).every(key =>
    isEqual(objectOne?.[key], objectTwo?.[key])
  )
}
